/**
 * The waitlist dialog of the way-in pages' header (design audit 2026-10-15 against Claude Design's copy of 14 Oct 2026,
 * 23:17 UTC: the Sign up / Log in screen's `.wh` header carries the `hwl` dialog, `hwlVals`). An industry or use case
 * marked Soon — in the Solutions mega menu and on the phone menu, where its badge reads "Waitlist" — opens it.
 *
 * The portal's copy of the public site's `components/WaitlistDialog.astro` and `site/waitlist.ts` (the apps never import
 * each other), drawn by React: "Coming soon", the solution's name, the intro, Work email, Country (Greece, Cyprus,
 * United Kingdom, Ireland, Other), "Your callers speak" (English, Ελληνικά, Other) and "Join the waitlist"; then
 * "You're on the list." / "We'll email you the day it goes live." / Done — shown only after the API stored the address
 * (`joinWaitlist`, `POST /v1/public/waitlist-signups`, `202`). Two lines are not the design's, as on the public site: a
 * request that failed, and a form whose words the API no longer holds (`409`).
 *
 * Opening: the form is empty, Country is Greece and "Your callers speak" is the portal's language (the design's
 * defaults); focus goes to Work email; the page under it does not scroll. Closing: ✕, Done, Escape or a press on the
 * scrim; focus returns to what opened it when that is still on screen. Tab and Shift+Tab stay inside.
 *
 * "Talk to us →" still goes to the Contact page, as on the public site: the design's "Something else?" form has no
 * field for what the visitor needs (the owner's call, the public site's report of 2026-10-14).
 */
import { ApiError } from "@wetalk/api-client";
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import type { WaitlistSignupRequest } from "../../api/slice/personal-security.js";
import { portalText, usePortalTranslation, type PortalKey } from "../../i18n/portal.js";

import type { HeaderSolution } from "./site-header-model.js";

/**
 * The revision of the form's words a visitor agrees to — the api's `WAITLIST_CONSENT_TEXT_REVISION`
 * (`apps/api/src/routes/public-waitlist/route.ts`) and the public site's. A change of the intro or the button is a new
 * revision in all three places, in one commit.
 */
export const WAITLIST_CONSENT_TEXT_REVISION = "waitlist-2026-10-14";

/** The root class that locks the page's scroll (the phone menu's, `public-frame.css`). */
const SCROLL_LOCK = "pd-scroll-locked";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** The design's own check (`hwlVals.submit`). */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

const COUNTRY = [
  { code: "GR", label: "site_header.waitlist_dialog.country_gr" },
  { code: "CY", label: "site_header.waitlist_dialog.country_cy" },
  { code: "GB", label: "site_header.waitlist_dialog.country_gb" },
  { code: "IE", label: "site_header.waitlist_dialog.country_ie" },
  { code: "other", label: "site_header.waitlist_dialog.country_elsewhere" },
] as const satisfies readonly { readonly code: WaitlistSignupRequest["countryCode"]; readonly label: PortalKey<"core"> }[];

type Problem = "empty" | "invalid" | "failed" | "stale";

export interface WaitlistOpening {
  readonly entry: HeaderSolution;
  readonly source: WaitlistSignupRequest["source"];
  /** What opened it, so focus goes back there on closing. */
  readonly opener: HTMLElement | null;
}

export function WaitlistDialog({ opening, onClose }: { readonly opening: WaitlistOpening; readonly onClose: () => void }): ReactNode {
  const { locale } = usePortalTranslation("core");
  const gateway = useGateway();
  const pageLanguage: "en" | "el" = locale === "el" ? "el" : "en";
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState<WaitlistSignupRequest["countryCode"]>("GR");
  const [callerLanguage, setCallerLanguage] = useState<WaitlistSignupRequest["callerLanguage"]>(pageLanguage);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [sending, setSending] = useState(false);
  const [stored, setStored] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const doneRef = useRef<HTMLButtonElement | null>(null);
  const mounted = useRef(true);
  const errorId = useId();
  const name = portalText("core", `site_header.solution.${opening.entry.id}.name`);

  const close = (): void => {
    onClose();
    const back = opening.opener;
    if (back !== null && back.isConnected && back.closest("[hidden]") === null && back.offsetParent !== null) back.focus({ preventScroll: true });
  };
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });

  // Focus to Work email; the page under it locked; Escape and the focus trap.
  useEffect(() => {
    mounted.current = true;
    emailRef.current?.focus();
    const html = document.documentElement;
    const scrollbar = Math.max(0, window.innerWidth - html.clientWidth);
    html.style.setProperty("--pd-scrollbar-width", `${String(scrollbar)}px`);
    html.classList.add(SCROLL_LOCK);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const box = boxRef.current;
      if (box === null) return;
      const inside = [...box.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((each) => each.closest("[hidden]") === null);
      const first = inside[0];
      const last = inside[inside.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (!(active instanceof Node) || !box.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      mounted.current = false;
      document.removeEventListener("keydown", onKey);
      html.classList.remove(SCROLL_LOCK);
      html.style.removeProperty("--pd-scrollbar-width");
    };
  }, []);

  useEffect(() => {
    if (stored) doneRef.current?.focus();
  }, [stored]);

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (sending) return;
    const value = email.trim();
    const emailProblem: Problem | null = value === "" ? "empty" : EMAIL_SHAPE.test(value) ? null : "invalid";
    setProblem(emailProblem);
    if (emailProblem !== null) {
      emailRef.current?.focus();
      return;
    }
    setSending(true);
    gateway
      .joinWaitlist({
        email: value,
        solutionCode: opening.entry.id,
        countryCode: country,
        callerLanguage,
        source: opening.source,
        pageLanguage,
        consentTextRevision: WAITLIST_CONSENT_TEXT_REVISION,
      })
      .then(
        () => {
          if (!mounted.current) return;
          setSending(false);
          setStored(true);
        },
        (raised: unknown) => {
          if (!mounted.current) return;
          setSending(false);
          setProblem(raised instanceof ApiError && raised.status === 409 ? "stale" : "failed");
        },
      );
  };

  const emailProblem = problem === "empty" || problem === "invalid";
  return (
    <div
      className="wh-wlb"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div ref={boxRef} className="wh-wl" role="dialog" aria-modal="true" aria-labelledby={stored ? "wh-wl-done-title" : "wh-wl-title"}>
        <button type="button" className="wh-wl-x" aria-label={portalText("core", "site_header.waitlist_dialog.close")} onClick={close}>
          <svg className="wh-icon" stroke="currentColor" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        {stored ? (
          <div>
            <p className="wh-wl-eye">{name}</p>
            <h2 id="wh-wl-done-title" className="wh-wl-title">
              {portalText("core", "site_header.waitlist_dialog.done_title")}
            </h2>
            <p className="wh-wl-intro">{portalText("core", "site_header.waitlist_dialog.done_intro")}</p>
            <button type="button" ref={doneRef} className="wh-wl-btn" onClick={close}>
              {portalText("core", "site_header.waitlist_dialog.done")}
            </button>
          </div>
        ) : (
          <div>
            <p className="wh-wl-eye">{portalText("core", "site_header.waitlist_dialog.eyebrow")}</p>
            <h2 id="wh-wl-title" className="wh-wl-title">
              {name}
            </h2>
            <p className="wh-wl-intro">{portalText("core", "site_header.waitlist_dialog.intro")}</p>
            <form className="wh-wl-form" noValidate onSubmit={onSubmit}>
              <div className="wh-f">
                <label htmlFor="wh-wl-email">{portalText("core", "site_header.waitlist_dialog.work_email")}</label>
                <input
                  id="wh-wl-email"
                  ref={emailRef}
                  className="wh-in-f"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder={portalText("core", "site_header.waitlist_dialog.email_placeholder")}
                  value={email}
                  aria-invalid={emailProblem}
                  aria-describedby={errorId}
                  onChange={(event) => {
                    setEmail(event.currentTarget.value);
                    if (emailProblem) setProblem(null);
                  }}
                />
                <span id={errorId} className="wh-ferr" role="alert" hidden={!emailProblem}>
                  {problem === "empty"
                    ? portalText("core", "site_header.waitlist_dialog.email_empty")
                    : problem === "invalid"
                      ? portalText("core", "site_header.waitlist_dialog.email_invalid")
                      : null}
                </span>
              </div>
              <div className="wh-f">
                <label htmlFor="wh-wl-country">{portalText("core", "site_header.waitlist_dialog.country")}</label>
                <select
                  id="wh-wl-country"
                  className="wh-in-f"
                  value={country}
                  onChange={(event) => {
                    setCountry(event.currentTarget.value as WaitlistSignupRequest["countryCode"]);
                  }}
                >
                  {COUNTRY.map((one) => (
                    <option key={one.code} value={one.code}>
                      {portalText("core", one.label)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="wh-f">
                <label htmlFor="wh-wl-lang">{portalText("core", "site_header.waitlist_dialog.callers_speak")}</label>
                <select
                  id="wh-wl-lang"
                  className="wh-in-f"
                  value={callerLanguage}
                  onChange={(event) => {
                    setCallerLanguage(event.currentTarget.value as WaitlistSignupRequest["callerLanguage"]);
                  }}
                >
                  {/* English and Ελληνικά in their own language, on both pages (as the language menu writes them). */}
                  <option value="en" lang="en">
                    {portalText("core", "site_header.waitlist_dialog.language_en")}
                  </option>
                  <option value="el" lang="el">
                    {portalText("core", "site_header.waitlist_dialog.language_el")}
                  </option>
                  <option value="other">{portalText("core", "site_header.waitlist_dialog.language_elsewhere")}</option>
                </select>
              </div>
              {problem === "failed" || problem === "stale" ? (
                <p className="wh-ferr" role="alert">
                  {problem === "failed" ? portalText("core", "site_header.waitlist_dialog.failed") : portalText("core", "site_header.waitlist_dialog.stale")}
                </p>
              ) : null}
              <button type="submit" className="wh-wl-btn" aria-busy={sending}>
                {portalText("core", "site_header.waitlist_dialog.join")}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
